import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { calcLifePath, calcNameNumber, RULING_PLANET } from "../lib/numerology";
import { titleCaseName } from "../lib/text";

// One-line teaser per Life Path — planet + archetype. Matches the language
// used in the real Sacred Report so paying customers see continuity.
const LIFE_PATH_TEASER = {
  1: "The Leader",
  2: "The Diplomat",
  3: "The Communicator",
  4: "The Builder",
  5: "The Explorer",
  6: "The Nurturer",
  7: "The Seeker",
  8: "The Achiever",
  9: "The Warrior",
  11: "The Illuminator (Master)",
  22: "The Architect (Master)",
  33: "The Healer (Master)",
};

const NAME_NUMBER_TEASER = {
  1: "Independent Spirit",
  2: "Peaceful Connector",
  3: "Creative Voice",
  4: "Steady Foundation",
  5: "Free Adventurer",
  6: "Devoted Caregiver",
  7: "Deep Thinker",
  8: "Powerful Achiever",
  9: "Compassionate Giver",
  11: "Intuitive Guide (Master)",
  22: "Practical Visionary (Master)",
  33: "Master Healer (Master)",
};

// 4-5 sentence positive mini-readings for the result card. Written in the
// Pancha Healing brand voice — warm, specific, premium, and consistent with
// the paid Sacred Report tone.
const LIFE_PATH_READING = {
  1: "You were born to lead, not follow. When you decide, the world quietly moves with you. Independence is not a preference for you \u2014 it is your power. The moment you own your voice, doors that were closed for years begin to open.",
  2: "You feel what others cannot say. Rooms soften when you enter them. Your gift is the art of harmony \u2014 pairing people, ideas, and moments in ways that finally make sense. When you trust your intuition instead of overthinking, life stops resisting you and starts flowing with you.",
  3: "Your words carry light. Whether you write, speak, teach, or simply post, people remember how you make them feel. Creativity is not a hobby for you \u2014 it is the fuel your soul runs on. When you express freely, luck and joy arrive in unexpected waves.",
  4: "You are the one who actually finishes things. Where others dream, you draft, plan, and execute. Steady, loyal, and quietly ambitious, you build lives, businesses, and legacies that outlast the noise. Discipline is your superpower, and this year rewards it richly.",
  5: "You cannot be caged, and you were never meant to be. Change energises you, travel calls you, new ideas light you up. Your quick mind and adaptable spirit make you the friend everyone wants and the mind every team needs. The more freedom you give yourself, the more life pays you back.",
  6: "You are the person people come home to. Love, beauty, family, and harmony are your natural elements. You have a rare gift for making ordinary moments feel sacred. When you pour into others without emptying yourself, your life becomes a soft, luminous kind of wealthy.",
  7: "You see what most people miss. Silence doesn\u2019t scare you \u2014 it teaches you. Behind your calm surface is a deep, wise inner world where the real answers live. Trust your instincts; they are older than logic and almost always right.",
  8: "You are wired for scale. Money, authority, and long-term legacy respond to you \u2014 not by luck, but by law. You take the long road, and it always pays. When you align your ambition with integrity, no ceiling in your life stays intact for long.",
  9: "You feel things deeply and fight for them fiercely. Justice, compassion, and courage run through your veins. You are here to complete cycles others couldn\u2019t \u2014 for your family, your lineage, and yourself. When you channel your fire into a mission, you become unstoppable.",
  11: "You are here to inspire, not just achieve. People feel your energy before they hear your words. Your intuition is a broadcast tower, not a whisper. Trust the visions you receive \u2014 they were given to you because you were built to carry them.",
  22: "You dream in blueprints. What others call impossible, you quietly turn into infrastructure. Big vision plus grounded execution \u2014 a rare, powerful combination. Your work is meant to touch generations, not just seasons.",
  33: "You are here to heal at scale \u2014 with words, touch, art, or presence. People soften around you without knowing why. Your compassion is a kind of light that reshapes rooms. Protect it fiercely; the world needs the version of you that stays whole.",
};

const NAME_NUMBER_READING = {
  1: "Your name carries the frequency of leadership. You attract respect naturally and rarely need to ask for it twice. When you back yourself first, everything else follows. Confidence is not vanity for you \u2014 it is destiny.",
  2: "Your name opens doors that force cannot. You collaborate, mediate, and unite where others clash. People trust you with their real thoughts, and that trust is your quiet currency. It compounds beautifully over time.",
  3: "Your name is a magnet for expression and joy. Words, art, and ideas want to move through you. Life smiles wider on you when you create instead of criticise. Your charm is not decoration \u2014 it is a serious spiritual asset.",
  4: "Your name carries the weight of stability. People instinctively feel safer around you. What you build lasts, and what you promise, you deliver. Slow and steady is not your limitation \u2014 it is your legacy.",
  5: "Your name vibrates with movement. You are wired for variety, travel, and reinvention. Rules that don\u2019t serve you rarely survive long in your life \u2014 and that\u2019s a feature, not a flaw. Freedom is your natural wealth.",
  6: "Your name is loved. People, relationships, homes, and even animals gravitate to you. Love is your currency, and it earns interest wherever you invest it. When you love yourself with the same devotion, life becomes luminous.",
  7: "Your name carries the frequency of wisdom. You process life through insight, not noise. Silence, study, and solitude sharpen you. Your quiet always speaks louder than everyone else\u2019s shouting.",
  8: "Your name is written in the language of results. Money, authority, and legacy respond to you when you show up with integrity. You are meant to lead, own, and scale. Play a bigger game \u2014 you can hold it.",
  9: "Your name radiates warmth. You care fiercely and give generously, and the universe notices. Protecting your energy while serving the world is your master lesson. When you master it, doors open worldwide.",
  11: "Your name carries a rare spiritual charge. People feel guided just being near you. You are here to inspire, not just impress. Follow the pulls only you can feel.",
  22: "Your name is built to build. Vision and execution live inside you at the same time \u2014 a combination most people spend a lifetime chasing. Think in decades, not months.",
  33: "Your name is a healing frequency. Words, touch, and presence all soften people around you. Guard your energy and use it wisely. What you offer changes lives.",
};

// Short one-liners used on the exported Share Card (canvas has less room).
const LIFE_PATH_LINE = {
  1: "Born to lead \u2014 others follow your certainty.",
  2: "Peacemaker with a rare, intuitive gift.",
  3: "A creative voice the world remembers.",
  4: "The builder \u2014 what you start, you finish.",
  5: "Free spirit wired for movement & change.",
  6: "The heart others come home to.",
  7: "You see what most people miss.",
  8: "Built for scale \u2014 money, power, legacy.",
  9: "The warrior of compassion and courage.",
  11: "An inspirer with a rare spiritual charge.",
  22: "A visionary who quietly builds the impossible.",
  33: "A healer whose presence changes rooms.",
};

const NAME_NUMBER_LINE = {
  1: "A name that leads and attracts respect.",
  2: "A name that unites and opens doors.",
  3: "A name that magnetises joy & expression.",
  4: "A name of stability and legacy.",
  5: "A name written in the language of freedom.",
  6: "A name deeply loved by people & places.",
  7: "A name carrying the frequency of wisdom.",
  8: "A name of authority and lasting results.",
  9: "A name that radiates warmth to the world.",
  11: "A name with an inspirational spiritual charge.",
  22: "A name built to build things that last.",
  33: "A name that heals just by being spoken.",
};

/**
 * QuickCalculator — free landing-page widget. Computes Life Path (from DOB) and
 * Name Number (Chaldean, from full name) using the exact same helpers the paid
 * prescription engine uses (`calcLifePath` / `calcNameNumber` from `lib/numerology`).
 * Fires a `CalculatorUsed` Meta Pixel custom event on each successful compute.
 */

// Load an image and resolve when it's ready. Used to inline the lotus emblem
// onto the share-card canvas.
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Draw a 1080x1080 share card matching the OG banner aesthetic. Returns a Blob.
async function renderShareCard({ firstName, lifePath, nameNumber, lpTeaser, nnTeaser, lpLine, nnLine, planet }) {
  // Ensure custom fonts are ready before drawing so the wordmark uses Cinzel Decorative.
  try { if (document.fonts && document.fonts.ready) { await document.fonts.ready; } } catch (_e) { /* ignore */ }

  const SIZE = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  // Background gradient (matches OG banner)
  const bg = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  bg.addColorStop(0, "#0F0A1F");
  bg.addColorStop(0.45, "#1A1030");
  bg.addColorStop(1, "#2C0A5E");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Soft violet halo
  const halo = ctx.createRadialGradient(SIZE / 2, SIZE * 0.4, 60, SIZE / 2, SIZE * 0.4, SIZE * 0.7);
  halo.addColorStop(0, "rgba(106, 13, 173, 0.55)");
  halo.addColorStop(1, "rgba(106, 13, 173, 0)");
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Subtle gold star sparkles
  ctx.fillStyle = "rgba(212, 168, 85, 0.75)";
  const sparkles = [
    [110, 220, 3], [980, 180, 2], [180, 900, 2], [920, 950, 3],
    [500, 130, 2], [820, 460, 2.5], [260, 500, 2], [640, 780, 2.5],
    [420, 220, 2], [960, 720, 2],
  ];
  sparkles.forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); });

  // Corner ornament brackets
  const drawCorner = (x, y, dx, dy) => {
    ctx.strokeStyle = "rgba(212, 168, 85, 0.7)";
    ctx.lineWidth = 2;
    const L = 74;
    ctx.beginPath(); ctx.moveTo(x + dx * L, y); ctx.lineTo(x, y); ctx.lineTo(x, y + dy * L); ctx.stroke();
  };
  drawCorner(56, 56, 1, 1); drawCorner(SIZE - 56, 56, -1, 1);
  drawCorner(56, SIZE - 56, 1, -1); drawCorner(SIZE - 56, SIZE - 56, -1, -1);

  // Lotus emblem (top-centre, circle-masked so square PNG dissolves cleanly)
  try {
    const emblem = await loadImage("/pancha-emblem.png");
    ctx.save();
    ctx.beginPath();
    ctx.arc(SIZE / 2, 270, 155, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(emblem, SIZE / 2 - 200, 70, 400, 400);
    ctx.restore();
  } catch (_e) { /* emblem optional */ }

  // Wordmark
  ctx.fillStyle = "#F5EFE6";
  ctx.textAlign = "center";
  ctx.font = "900 62px 'Cinzel Decorative', 'Cinzel', serif";
  ctx.fillText("Pancha Healing", SIZE / 2, 500);

  // Gold divider
  ctx.strokeStyle = "#D4A855";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(SIZE / 2 - 60, 528); ctx.lineTo(SIZE / 2 + 60, 528); ctx.stroke();

  // Greeting
  ctx.fillStyle = "#D4A855";
  ctx.font = "italic 34px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText(`${firstName}\u2019s numbers`, SIZE / 2, 585);

  // Life Path card
  const cardW = 820, cardH = 168, cardY1 = 605;
  const cardX = (SIZE - cardW) / 2;
  ctx.fillStyle = "rgba(75, 26, 138, 0.55)";
  ctx.strokeStyle = "rgba(212, 168, 85, 0.55)";
  ctx.lineWidth = 1.5;
  roundRect(ctx, cardX, cardY1, cardW, cardH, 22, true, true);

  ctx.fillStyle = "#D4A855";
  ctx.textAlign = "left";
  ctx.font = "600 20px 'Cinzel', serif";
  ctx.fillText("LIFE PATH", cardX + 40, cardY1 + 40);
  ctx.fillStyle = "#F5EFE6";
  ctx.font = "600 32px 'Cinzel', serif";
  ctx.fillText(`${lifePath} \u2014 ${planet}, ${lpTeaser}`, cardX + 40, cardY1 + 82);
  // Short positive reading
  ctx.fillStyle = "rgba(245, 239, 230, 0.85)";
  ctx.font = "italic 22px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText(lpLine || "", cardX + 40, cardY1 + 130);

  // Name Number card
  const cardY2 = cardY1 + cardH + 22;
  ctx.fillStyle = "rgba(212, 168, 85, 0.14)";
  ctx.strokeStyle = "rgba(212, 168, 85, 0.65)";
  roundRect(ctx, cardX, cardY2, cardW, cardH, 22, true, true);

  ctx.fillStyle = "#D4A855";
  ctx.font = "600 20px 'Cinzel', serif";
  ctx.fillText("NAME NUMBER", cardX + 40, cardY2 + 40);
  ctx.fillStyle = "#F5EFE6";
  ctx.font = "600 32px 'Cinzel', serif";
  ctx.fillText(`${nameNumber} \u2014 ${nnTeaser}`, cardX + 40, cardY2 + 82);
  // Short positive reading
  ctx.fillStyle = "rgba(245, 239, 230, 0.9)";
  ctx.font = "italic 22px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText(nnLine || "", cardX + 40, cardY2 + 130);

  // Footer
  ctx.textAlign = "center";
  ctx.fillStyle = "#D4A855";
  ctx.font = "italic 28px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText("\u2726  Find your numbers at panchahealing.com  \u2726", SIZE / 2, 1010);

  // Subtle watermark (small gold, low opacity, elegant)
  ctx.save();
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = "#D4A855";
  ctx.font = "500 16px 'Cinzel', serif";
  ctx.fillText("Powered by Pancha Healing  \u2022  panchahealing.com", SIZE / 2, 1050);
  ctx.restore();

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
}

// Rounded-rectangle helper for canvas (no polyfill dependency).
function roundRect(ctx, x, y, w, h, r, fill, stroke) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  if (fill) ctx.fill();
  if (stroke) ctx.stroke();
}

// Sensible defaults for the DOB picker on iOS Safari — the wheel opens at
// whatever value the input holds, so we seed it to Jan 1 1995 (~30 y old) to
// avoid forcing users to scroll back decades from today. The user-facing
// state (`dobTouched`) tracks whether they actually picked a date.
const DOB_SEED = "1995-01-01";
const DOB_MAX = new Date().toISOString().slice(0, 10);
const DOB_MIN = "1930-01-01";

export default function QuickCalculator({ compact = false }) {
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState(DOB_SEED);
  const [dobTouched, setDobTouched] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [sharing, setSharing] = useState(false);
  const resultRef = useRef(null);

  const handleShare = async () => {
    if (!result || sharing) return;
    setSharing(true);
    try {
      const firstName = titleCaseName((fullName.trim().split(/\s+/)[0]) || "You");
      const blob = await renderShareCard({
        firstName,
        lifePath: result.lifePath,
        nameNumber: result.nameNumber,
        lpTeaser: LIFE_PATH_TEASER[result.lifePath] || "",
        nnTeaser: NAME_NUMBER_TEASER[result.nameNumber] || "",
        lpLine: LIFE_PATH_LINE[result.lifePath] || "",
        nnLine: NAME_NUMBER_LINE[result.nameNumber] || "",
        planet: RULING_PLANET[result.lifePath] || "",
      });

      // Meta Pixel — share card generated
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("trackCustom", "ShareCardCreated", {
          life_path: result.lifePath,
          name_number: result.nameNumber,
        });
      }

      const fileName = `pancha-healing-${firstName.toLowerCase()}-numbers.png`;
      const file = new File([blob], fileName, { type: "image/png" });

      // Prefer native share sheet (mobile) so Instagram/WhatsApp share is 1 tap
      if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "My Pancha Healing numbers", text: "Just found my Life Path & Name Number \u2014 discover yours at panchahealing.com" });
          return;
        } catch (_e) {
          // user cancelled or share failed — fall through to download
        }
      }

      // Fallback: download to device
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (_e) {
      setError("Couldn't create your share card. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  const compute = () => {
    setError("");
    const nameTrimmed = fullName.trim();
    if (!nameTrimmed) return setError("Please enter your full birth name.");
    if (!dobTouched) return setError("Please pick your date of birth.");
    // Future-date guard — iOS date input accepts today by default; block anything > now.
    if (dob > DOB_MAX) return setError("Please enter your real date of birth.");
    if (dob < DOB_MIN) return setError("Please enter a birth year between 1930 and today.");
    const lifePath = calcLifePath(dob);
    const nameNumber = calcNameNumber(nameTrimmed);
    if (!lifePath || !nameNumber) return setError("Couldn't calculate — please check your entries.");

    setResult({ lifePath, nameNumber });

    // Persist for pre-fill on /prescription so the customer doesn't retype.
    try {
      sessionStorage.setItem(
        "pancha.widget_prefill",
        JSON.stringify({ fullName: nameTrimmed, dob })
      );
    } catch (_e) { /* private mode / disabled — silent ignore */ }

    // Meta Pixel custom event
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", "CalculatorUsed", {
        life_path: lifePath,
        name_number: nameNumber,
      });
    }

    // Scroll the result card into view on mobile
    setTimeout(() => {
      if (resultRef.current) resultRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  return (
    <section
      className={`relative ${compact ? "pt-6 pb-10 md:pt-10 md:pb-14" : "py-16 md:py-20"}`}
      style={{
        background: compact ? "transparent" : "linear-gradient(180deg, #FAF7F2 0%, #F3EDE0 100%)",
      }}
      data-testid="quick-calculator"
    >
      <div className="max-w-2xl mx-auto px-5 md:px-10">
        <div className="text-center">
          <div className={`ph-overline ${compact ? "text-[#F5D989]" : ""}`}>Your Free Reading</div>
          <h2 className={`font-serif mt-2 ${compact ? "text-2xl md:text-4xl text-white" : "text-3xl md:text-5xl text-[#2C0A5E]"}`}>
            Which Number Are You?
          </h2>
          <p className={`font-serif italic mt-3 ${compact ? "text-white/80 text-sm md:text-base" : "text-[#6B5E7E]"}`}>
            Enter your birth name and date of birth. See your Life Path number and Name Number in seconds &mdash; before your bracelet is prescribed.
          </p>
        </div>

        <div
          className="mt-8 rounded-2xl p-6 md:p-8 shadow-md"
          style={{
            background: "#FFFFFF",
            border: "1px solid rgba(212,168,85,0.35)",
          }}
        >
          <div className="grid gap-4">
            <label className="block">
              <span className="text-xs tracking-[0.22em] uppercase text-[#6B5E7E]">Full Name (as on birth records)</span>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="e.g. Priya Sharma"
                className="mt-2 w-full h-12 rounded-xl border border-[#D4C7EA] px-4 text-[#2C0A5E] focus:outline-none focus:border-[#6A0DAD] transition"
                data-testid="calc-name-input"
                autoComplete="name"
              />
            </label>
            <label className="block">
              <span className="text-xs tracking-[0.22em] uppercase text-[#6B5E7E]">Date of Birth</span>
              <div className="calc-dob-wrap mt-2">
                <input
                  type="date"
                  value={dob}
                  min={DOB_MIN}
                  max={DOB_MAX}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDob(v || DOB_SEED);
                    setDobTouched(!!v);
                    // Inline gentle error the moment iOS accepts a future value
                    if (v && v > DOB_MAX) setError("Please enter your real date of birth.");
                    else if (v && v < DOB_MIN) setError("Please enter a birth year between 1930 and today.");
                    else setError("");
                  }}
                  className={`calc-dob-input${!dobTouched ? " calc-dob-input--empty" : ""} h-12 rounded-xl border border-[#D4C7EA] px-4 text-[#2C0A5E] focus:outline-none focus:border-[#6A0DAD] transition`}
                  data-testid="calc-dob-input"
                  autoComplete="bday"
                  aria-label="Date of Birth"
                />
                {/* iOS Safari native date inputs render an empty grey box until
                    tapped. When empty (before user has actually picked a date),
                    we seed the wheel to 1995-01-01 so users don't have to scroll
                    back decades from today. The visible overlay below keeps the
                    UI looking intentional. */}
                {!dobTouched && (
                  <span className="calc-dob-placeholder" aria-hidden data-testid="calc-dob-placeholder">
                    DD / MM / YYYY
                  </span>
                )}
              </div>
            </label>

            {error && (
              <p className="text-sm text-[#C13B1B]" data-testid="calc-error">{error}</p>
            )}

            <button
              type="button"
              onClick={compute}
              data-testid="calc-reveal-btn"
              className="mt-2 w-full h-12 rounded-xl font-medium tracking-wide"
              style={{
                background: "#6A0DAD",
                color: "#FFFFFF",
                fontFamily: "'Cinzel', serif",
                letterSpacing: "1px",
                fontSize: "15px",
                boxShadow: "0 6px 18px rgba(106,13,173,0.22)",
              }}
            >
              Reveal My Numbers &rarr;
            </button>

            {/* Privacy reassurance — eases first-visit hesitation about giving DOB.
               Card background is always white (both compact + full variants), so we
               always use the dark lavender-grey for contrast. */}
            <p
              className="mt-3 text-[11px] md:text-xs text-center leading-relaxed text-[#6B5E7E]"
              data-testid="calc-privacy-note"
            >
              <span aria-hidden style={{ marginRight: 4 }}>&#128274;</span>
              Used only to calculate your Life Path and Name Number &mdash; never shared.
            </p>
          </div>

          {result && (
            <div ref={resultRef} className="mt-6 grid gap-4" data-testid="calc-result">
              <div
                className="rounded-2xl p-5"
                style={{ background: "linear-gradient(135deg, #2C0A5E 0%, #4B1A8A 100%)", color: "#FAF7F2" }}
                data-testid="calc-life-path-card"
              >
                <div className="flex items-center gap-4">
                  <div
                    className="shrink-0 w-16 h-16 rounded-full flex items-center justify-center font-serif text-3xl"
                    style={{ background: "rgba(212,168,85,0.18)", border: "1px solid #D4A855", color: "#D4A855" }}
                  >
                    {result.lifePath}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "#D4A855" }}>Life Path</div>
                    <div className="font-serif text-lg mt-0.5">
                      Life Path {result.lifePath} &mdash; {RULING_PLANET[result.lifePath] || "\u2014"}, {LIFE_PATH_TEASER[result.lifePath] || ""}
                    </div>
                  </div>
                </div>
                <p
                  className="font-serif mt-3 leading-relaxed"
                  style={{ color: "rgba(245,239,230,0.92)", fontSize: "15px" }}
                  data-testid="calc-life-path-reading"
                >
                  {LIFE_PATH_READING[result.lifePath] || ""}
                </p>
              </div>

              <div
                className="rounded-2xl p-5"
                style={{ background: "linear-gradient(135deg, #F3EDE0 0%, #E9DFC7 100%)", border: "1px solid rgba(212,168,85,0.5)", color: "#2C0A5E" }}
                data-testid="calc-name-number-card"
              >
                <div className="flex items-center gap-4">
                  <div
                    className="shrink-0 w-16 h-16 rounded-full flex items-center justify-center font-serif text-3xl"
                    style={{ background: "#FFFFFF", border: "1px solid #D4A855", color: "#6A0DAD" }}
                  >
                    {result.nameNumber}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "#8A6D2F" }}>Name Number</div>
                    <div className="font-serif text-lg mt-0.5">
                      Name Number {result.nameNumber} &mdash; {NAME_NUMBER_TEASER[result.nameNumber] || ""}
                    </div>
                  </div>
                </div>
                <p
                  className="font-serif mt-3 leading-relaxed"
                  style={{ color: "#4B3A6E", fontSize: "15px" }}
                  data-testid="calc-name-number-reading"
                >
                  {NAME_NUMBER_READING[result.nameNumber] || ""}
                </p>
              </div>

              <p
                className="font-serif italic text-center mt-2 leading-relaxed"
                style={{ color: "#6B5E7E", fontSize: "15px" }}
                data-testid="calc-curiosity-cta"
              >
                There&rsquo;s more your numbers know &mdash; including the crystals they call for.
              </p>

              <button
                type="button"
                onClick={handleShare}
                disabled={sharing}
                data-testid="calc-save-card-btn"
                className="w-full h-12 rounded-xl font-medium tracking-wide"
                style={{
                  background: "#FFFFFF",
                  color: "#6A0DAD",
                  border: "1.5px solid #D4A855",
                  fontFamily: "'Cinzel', serif",
                  letterSpacing: "1px",
                  fontSize: "14px",
                  boxShadow: "0 4px 14px rgba(212,168,85,0.18)",
                  opacity: sharing ? 0.7 : 1,
                  cursor: sharing ? "wait" : "pointer",
                }}
              >
                <span aria-hidden style={{ color: "#D4A855", marginRight: "8px" }}>&#10022;</span>
                {sharing ? "Creating your card\u2026" : "Save My Numbers"}
              </button>

              <Link
                to="/prescription"
                className="ph-cta w-full justify-center"
                data-testid="calc-cta-prescription"
              >
                See The Crystals They Prescribe
                <span className="arrow" aria-hidden>&rarr;</span>
              </Link>

              <p className="text-center text-[11px] tracking-wider" style={{ color: "#9e94ae" }}>
                <span aria-hidden style={{ color: "#D4A855" }}>&#10022;</span> Your details are saved so you won&rsquo;t need to retype
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
